import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { STATUS, STATUS_ORDER, SOURCE } from '../constants';
import { newOrderId, upsertOrder, detectRepeatCustomer } from '../services/ordersService';

const emptyItem = () => ({ product: '', variation: '', buyerNotes: '', quantity: 1, unitPrice: '' });

const todayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Dialog to create a manual "direct" order (returning customers off-platform). */
export default function ManualOrderForm({ open, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  // Initialise fresh each time it opens.
  React.useEffect(() => {
    if (open) {
      setForm({
        orderedDate: todayISO(),
        status: STATUS.UNCUT,
        buyerName: '',
        email: '',
        phone: '',
        shippingName: '',
        address1: '',
        address2: '',
        townCity: '',
        county: '',
        postcode: '',
        country: 'United Kingdom',
        service: '',
        postage: '',
        discount: '',
        egNotes: '',
        items: [emptyItem()],
      });
    }
  }, [open]);

  if (!form) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setItem = (i, key) => (e) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, n) => (n === i ? { ...it, [key]: e.target.value } : it)),
    }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, n) => n !== i) }));

  const valid = form.buyerName.trim() && form.items.some((it) => it.product.trim());

  async function save() {
    setSaving(true);
    try {
      const id = newOrderId();
      const core = {
        source: SOURCE.DIRECT,
        platformOrderId: id,
        buyer: { userId: '', name: form.buyerName.trim(), email: form.email.trim() },
        shipping: {
          name: form.shippingName.trim() || form.buyerName.trim(),
          phone: form.phone.trim(),
          address1: form.address1.trim(),
          address2: form.address2.trim(),
          townCity: form.townCity.trim(),
          county: form.county.trim(),
          postcode: form.postcode.trim(),
          country: form.country.trim(),
          service: form.service.trim(),
          ioss: false,
        },
        lineItems: form.items
          .filter((it) => it.product.trim())
          .map((it, n) => ({
            lineId: `li_${n + 1}`,
            product: it.product.trim(),
            productRaw: it.product.trim(),
            variation: it.variation.trim(),
            buyerNotes: it.buyerNotes.trim(),
            quantity: Number(it.quantity) || 1,
            unitPrice: Number(it.unitPrice) || 0,
            isLeather: it.product.toLowerCase().includes('leather'),
          })),
        money: {
          currency: 'GBP',
          postage: Number(form.postage) || 0,
          discount: Number(form.discount) || 0,
          refund: 0,
        },
        dates: { ordered: new Date(form.orderedDate), postByPlatform: null },
        workflow: { status: form.status, egNotes: form.egNotes.trim() },
        meta: { manualEntry: true },
      };

      const repeat = await detectRepeatCustomer({
        name: core.buyer.name,
        email: core.buyer.email,
        address1: core.shipping.address1,
        excludeDocId: `${SOURCE.DIRECT}_${id}`,
      });

      await upsertOrder({ ...core, repeatCustomer: repeat });
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      console.error('Manual order save failed', e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>New direct order</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField label="Order date" type="date" size="small" value={form.orderedDate} onChange={set('orderedDate')} InputLabelProps={{ shrink: true }} />
          <TextField select label="Status" size="small" value={form.status} onChange={set('status')} sx={{ minWidth: 160 }}>
            {STATUS_ORDER.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Stack>

        <Typography variant="subtitle2" gutterBottom>Buyer</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
          <TextField label="Buyer name *" size="small" value={form.buyerName} onChange={set('buyerName')} />
          <TextField label="Email" size="small" value={form.email} onChange={set('email')} />
          <TextField label="Phone" size="small" value={form.phone} onChange={set('phone')} />
        </Box>

        <Typography variant="subtitle2" gutterBottom>Shipping</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <TextField label="Recipient name" size="small" value={form.shippingName} onChange={set('shippingName')} placeholder={form.buyerName} />
          <TextField label="Delivery service" size="small" value={form.service} onChange={set('service')} />
          <TextField label="Address 1" size="small" value={form.address1} onChange={set('address1')} />
          <TextField label="Address 2" size="small" value={form.address2} onChange={set('address2')} />
          <TextField label="Town/City" size="small" value={form.townCity} onChange={set('townCity')} />
          <TextField label="County" size="small" value={form.county} onChange={set('county')} />
          <TextField label="Postcode" size="small" value={form.postcode} onChange={set('postcode')} />
          <TextField label="Country" size="small" value={form.country} onChange={set('country')} />
        </Box>

        <Typography variant="subtitle2" gutterBottom>Line items</Typography>
        {form.items.map((it, i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 2fr 2fr 0.6fr 0.8fr auto' }, gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField label="Product *" size="small" value={it.product} onChange={setItem(i, 'product')} />
            <TextField label="Variation" size="small" value={it.variation} onChange={setItem(i, 'variation')} />
            <TextField label="Buyer notes" size="small" value={it.buyerNotes} onChange={setItem(i, 'buyerNotes')} />
            <TextField label="Qty" type="number" size="small" value={it.quantity} onChange={setItem(i, 'quantity')} />
            <TextField label="Price" type="number" size="small" value={it.unitPrice} onChange={setItem(i, 'unitPrice')} />
            <IconButton size="small" onClick={() => removeItem(i)} disabled={form.items.length === 1}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addItem} sx={{ mb: 2 }}>
          Add line item
        </Button>

        <Divider sx={{ my: 1 }} />
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField label="Postage" type="number" size="small" value={form.postage} onChange={set('postage')} />
          <TextField label="Discount" type="number" size="small" value={form.discount} onChange={set('discount')} />
        </Stack>
        <TextField label="EG notes" size="small" fullWidth multiline value={form.egNotes} onChange={set('egNotes')} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!valid || saving}>
          {saving ? 'Saving…' : 'Create order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
